'use client'

import Lightbox from 'yet-another-react-lightbox'
import Captions from 'yet-another-react-lightbox/plugins/captions'
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import 'yet-another-react-lightbox/plugins/captions.css'
import 'yet-another-react-lightbox/plugins/thumbnails.css'
import { useTheme } from '@mui/material/styles'
import { useMemo } from 'react'
import PropTypes from 'prop-types'
import styles from './FormViewer.module.scss'

export const FormViewer = ({ open, onClose, formName, previewUrls, pdfUrl }) => {
  const theme = useTheme()

  const slides = useMemo(() => {
    if (pdfUrl) {
      return [{ type: 'pdf', src: pdfUrl, title: formName }]
    }

    return (previewUrls ?? []).map((p, i) => ({
      src: p.previewUrl,
      thumbnail: p.thumbnailUrl,
      title: `${formName} - Page ${i + 1}`
    }))
  }, [previewUrls, pdfUrl, formName])

  return (
    <Lightbox
      open={open}
      close={onClose}
      slides={slides}
      // preload all slides so the Thumbnails plugin renders a thumbnail for every page
      // (it only renders thumbnails for slides within the carousel's preload window)
      carousel={{ finite: true, preload: slides.length }}
      render={{
        slide: ({ slide }) =>
          slide.type === 'pdf' ? (
            <iframe
              src={slide.src}
              title={formName || 'Document preview'}
              style={{ width: '100%', height: '100%', border: 0 }}
            />
          ) : undefined
      }}
      plugins={[Captions, Zoom, Thumbnails]}
      captions={{ showToggle: false, descriptionTextAlign: 'start' }}
      thumbnails={{
        position: 'bottom',
        width: 80,
        height: 100,
        border: 1,
        opacity: 0.3,
        borderRadius: 4,
        gap: 12,
        imageFit: 'cover',
        showToggle: true
      }}
      zoom={{ maxZoomPixelRatio: 4 }}
      className={styles.lightbox}
      styles={{
        root: {
          '--yarl__color_backdrop': theme.palette.support.navy,
          '--yarl__color_button': theme.palette.primary.contrast,
          '--yarl__color_button_active': theme.palette.primary.contrast,
          '--yarl__thumbnails_thumbnail_border_active_color': theme.palette.secondary.main,
          '--yarl__thumbnails_container_background_color': theme.palette.support.navy,
          fontFamily: theme.typography.fontFamily
        },
        slide: { padding: '80px 20px 20px 20px' }
      }}
    />
  )
}

FormViewer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  formName: PropTypes.string,
  previewUrls: PropTypes.arrayOf(
    PropTypes.shape({
      previewUrl: PropTypes.string.isRequired,
      thumbnailUrl: PropTypes.string.isRequired
    })
  ),
  pdfUrl: PropTypes.string
}
